// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import getInvoicesTermDiscount from "@store/Framework/Common/Utilities/getInvoicesTermDiscount";
import getInvoicesTotal from "@store/Framework/Common/Utilities/getInvoicesTotal";
import {
    InvoicePaymentContext,
    InvoicePaymentContextModel,
} from "@store/Framework/Components/InvoicePaymentContext/InvoicePaymentContext";
import { InvoicePaymentSuccessModel } from "@store/Framework/Types/ApiModels";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import * as React from "react";
import { FC, useEffect, useState } from "react";

export interface InvoicePaymentPageProps extends PageProps {}

export interface InvoicePaymentQueryParam {
    queryString: string;
    invoiceUniqueNumber: string;
    invoiceTimestamp: string;
}

const InvoicePaymentPage: FC<InvoicePaymentPageProps> = ({ id }) => {
    const dispatch = useAppDispatch();
    const { invoices } = useQueryString();
    const invoicePaymentQueryStrings = invoices?.split(",").filter(x => x) || [];
    const failedToLoadInvoices = useAppSelector(state => state.data.invoices.failedToLoadInvoices);
    const invalidInvoicePaymentQueries: string[] = [];
    const invoicesToPay: InvoiceModel[] = [];
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
    const [paymentSuccess, setPaymentSuccess] = useState<InvoicePaymentSuccessModel>();

    const invoicePaymentQueryParams: InvoicePaymentQueryParam[] = invoicePaymentQueryStrings.map(queryString => {
        const [invoiceUniqueNumber, invoiceTimestamp] = queryString.split(":");
        return { queryString, invoiceUniqueNumber, invoiceTimestamp };
    });

    const invoicesDataView = useAppSelector(state => getInvoicesDataView(state, {}));
    const isLoadingInvoices = !invoicesDataView.value || invoicesDataView.isLoading;
    useEffect(() => {
        if (!invoicesDataView.value && !invoicesDataView.isLoading) {
            dispatch(loadInvoices({}));
        }
    }, []);

    const countriesDataView = useAppSelector(state => getCountriesDataView(state, loadCurrentCountriesParameter));
    const isLoadingCountries = !countriesDataView.value || countriesDataView.isLoading;
    useEffect(() => {
        if (!countriesDataView.value && !countriesDataView.isLoading) {
            dispatch(loadCurrentCountries());
        }
    }, []);

    const currentCart = useAppSelector(getCurrentCartState);
    const isLoadingCart = !currentCart.value || currentCart.isLoading;
    useEffect(() => {
        if (!currentCart.value && !currentCart.isLoading) {
            dispatch(loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false }));
        }
    }, []);

    if (invoicesDataView.value) {
        invoicePaymentQueryParams.forEach(param => {
            const match = invoicesDataView.value!.find(inv => {
                const invTimestamp = new Date(inv.invoiceDate).getTime();
                return (
                    inv.invoiceUniqueNumber === param.invoiceUniqueNumber &&
                    invTimestamp.toString() === param.invoiceTimestamp
                );
            });

            if (!match || match.dueAmount === 0) {
                invalidInvoicePaymentQueries.push(param.queryString);
            } else {
                invoicesToPay.push(match);
            }
        });
    }

    let errorMessage = "";
    if (!invoicePaymentQueryStrings.length) {
        errorMessage = translate("You have not selected any invoice to pay.");
    } else if (failedToLoadInvoices) {
        errorMessage = translate("An error has occured. Please try again later.");
    } else if (invalidInvoicePaymentQueries.length && !invoicesToPay.length) {
        errorMessage = translate("Selected invoice(s) do not exist or have already been paid.");
    }

    const invoicePaymentContext: InvoicePaymentContextModel = {
        errorMessage,
        invalidInvoiceIds: invalidInvoicePaymentQueries,
        invoiceIds: invoicePaymentQueryStrings,
        invoices: invoicesToPay,
        isLoading: isLoadingInvoices || isLoadingCountries || isLoadingCart,
        isSubmitting,
        paymentErrorMessage,
        paymentSuccess,
        setIsSubmitting,
        setPaymentErrorMessage,
        setPaymentSuccess,
        termDiscount: getInvoicesTermDiscount(invoicesToPay),
        total: getInvoicesTotal(invoicesToPay),
    };

    return (
        <Page>
            <InvoicePaymentContext.Provider value={invoicePaymentContext}>
                <Zone contentId={id} zoneName="Content" />
            </InvoicePaymentContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: InvoicePaymentPage,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;
